// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { esc, joules, mag as fmtMag, num } from '../format';
import {
  bValue, depthCategory, energyConcentration, feltVerdict, groupBy, totalEnergy,
} from '../model';
import { quakes, regionOf, state } from '../state';
import { emptyState } from './shared';
import { periodNoun } from './shared';

type Sev = 'info' | 'warn' | 'alert';
interface Insight { sev: Sev; icon: string; title: string; body: string }

export function renderInsights(root: HTMLElement): void {
  const list = quakes();
  if (list.length === 0) { root.innerHTML = emptyState(); return; }

  const insights: Insight[] = [];
  const largest = list.reduce((a, b) => (b.mag > a.mag ? b : a));
  const conc = energyConcentration(list);
  const totalE = totalEnergy(list);
  const tsunamis = list.filter((q) => q.tsunami);
  const alerts = list.filter((q) => q.alert === 'orange' || q.alert === 'red');
  const shallow = list.filter((q) => depthCategory(q.depth) === 'Shallow');
  const deep = list.filter((q) => depthCategory(q.depth) === 'Deep');
  const felt = list.filter((q) => q.felt > 0).sort((a, b) => b.felt - a.felt);
  const blasts = list.filter((q) => q.type !== 'earthquake');
  const regions = groupBy(list, regionOf);
  const topRegion = regions[0];
  const b = bValue(list, 0.1);
  const noun = periodNoun(state.period);

  insights.push({
    sev: largest.mag >= 6 ? 'warn' : 'info',
    icon: '◆',
    title: `Largest event: magnitude ${fmtMag(largest.mag)}`,
    body: `Near ${esc(largest.place)}. ${esc(feltVerdict(largest.mag))} It alone accounts for about ${Math.round(conc * 100)}% of all seismic energy released in the past ${noun}.`,
  });

  if (conc >= 0.5) {
    insights.push({
      sev: 'info',
      icon: '⚡',
      title: `Energy is highly concentrated`,
      body: `The single biggest quake released ${Math.round(conc * 100)}% of the ${joules(totalE)} total. Because magnitude is logarithmic, a handful of large events dwarf thousands of small ones.`,
    });
  }

  if (alerts.length > 0) {
    insights.push({
      sev: 'alert',
      icon: '⚠',
      title: `${alerts.length} event${alerts.length === 1 ? '' : 's'} with a high impact alert`,
      body: `USGS PAGER flags ${alerts.length} event${alerts.length === 1 ? '' : 's'} as orange or red — meaning significant casualties or damage are estimated to be possible. The strongest is M${fmtMag(alerts[0].mag)} near ${esc(alerts[0].place)}.`,
    });
  }

  if (tsunamis.length > 0) {
    const big = tsunamis.reduce((a, c) => (c.mag > a.mag ? c : a));
    insights.push({
      sev: big.mag >= 7 ? 'alert' : 'warn',
      icon: '≈',
      title: `${tsunamis.length} quake${tsunamis.length === 1 ? '' : 's'} in a tsunami-capable zone`,
      body: `These occurred where a tsunami could, in principle, form (largest M${fmtMag(big.mag)} near ${esc(big.place)}). This is not an official warning — those come from tsunami warning centres.`,
    });
  }

  if (topRegion) {
    insights.push({
      sev: 'info',
      icon: '⌖',
      title: `Most active region: ${esc(topRegion.key)}`,
      body: `${num(topRegion.count)} of ${num(list.length)} events (${Math.round((topRegion.count / list.length) * 100)}%) sit on this plate, with the largest at M${fmtMag(topRegion.maxMag)}.`,
    });
  }

  insights.push({
    sev: 'info',
    icon: '▽',
    title: `${Math.round((shallow.length / list.length) * 100)}% of quakes are shallow`,
    body: `${num(shallow.length)} events began in the top 70 km of crust${deep.length ? `, while ${num(deep.length)} were deep (over 300 km)` : ''}. Shallow quakes shake the surface hardest for a given magnitude.`,
  });

  if (felt.length > 0) {
    insights.push({
      sev: 'info',
      icon: '☺',
      title: `${num(felt[0].felt)} people reported feeling one quake`,
      body: `The most widely-felt event was M${fmtMag(felt[0].mag)} near ${esc(felt[0].place)}, with ${num(felt[0].felt)} "Did You Feel It?" reports. ${num(felt.length)} events drew at least one report.`,
    });
  }

  if (Number.isFinite(b)) {
    insights.push({
      sev: 'info',
      icon: 'β',
      title: `b-value is ${b.toFixed(2)}`,
      body: `The slope of the Gutenberg–Richter frequency law for this window. Values near 1.0 are typical worldwide; it tells you roughly how many small quakes accompany each large one.`,
    });
  }

  if (blasts.length > 0) {
    insights.push({
      sev: 'info',
      icon: '✷',
      title: `${blasts.length} non-earthquake event${blasts.length === 1 ? '' : 's'}`,
      body: `USGS also catalogues human-made ground shaking — quarry blasts and explosions. QuakeWatch labels these separately so they aren’t mistaken for natural earthquakes.`,
    });
  }

  root.innerHTML = `
    <div class="panel">
      <div class="panel-title">What the data is telling us</div>
      <div class="panel-sub">Automatically detected from the ${num(list.length)} earthquakes in the past ${noun}. Findings refresh with the live feed.</div>
    </div>
    <div class="grid" style="grid-template-columns:repeat(auto-fill,minmax(min(320px,100%),1fr));margin-top:1rem">
      ${insights.map(card).join('')}
    </div>`;
}

function card(i: Insight): string {
  return `<div class="insight-card ${i.sev}">
    <div class="insight-icon">${i.icon}</div>
    <div class="insight-body"><h4>${esc(i.title)}</h4><p>${i.body}</p></div>
  </div>`;
}
